import { DeepActivity, DeepActivityPeriod } from 'oas';
import { FocusFile, FocusTag } from '../shared/file-importer/types';
import { ImportSelections } from './use-focus-import-selections';
import { TIMING_OPTION } from './constants';

interface TimeAlignment {
    period: DeepActivityPeriod | undefined;
    alignmentTagStartTime: number;
}

interface AnnotationTimingInfo {
    startTime: number;
    endTime: number;
    positionTime: number;
    alignedPeriod: DeepActivityPeriod | undefined;
}

export const getOffsetInMilliseconds = (focusFile: FocusFile | undefined, tag: FocusTag | undefined): number => {
    const sessionInputSource = focusFile?.session.sessionInput_sources.find(
        (source) => source.inputId === tag?.tagInput_id,
    );
    if (sessionInputSource) {
        return sessionInputSource.inputOffset * 1000;
    }

    return 0;
};

export const getAnnotationTimingInfo = (
    alignments: TimeAlignment[],
    importTag: FocusTag,
    focusFile: FocusFile | undefined,
): AnnotationTimingInfo => {
    const importTagOffset = getOffsetInMilliseconds(focusFile, importTag);
    const importTagStartTime = importTag.tagStart + importTagOffset;
    const importTagEndTime = importTag.tagEnd + importTagOffset;
    const importTagPosTime = importTag.tagPosition + importTagOffset;

    let alignedPeriod = undefined;
    let startTime = importTagStartTime;
    let endTime = importTagEndTime;
    let positionTime = importTagPosTime;
    const alignment = findAlignment(alignments, importTagStartTime);

    if (alignment) {
        const diff = importTagStartTime - alignment.alignmentTagStartTime;
        const positionDiff = importTagPosTime - importTagStartTime;
        const duration = importTagEndTime - importTagStartTime;

        const periodStartTime = alignment.period?.start_time ?? 0;

        startTime = periodStartTime + diff;
        endTime = startTime + duration;
        positionTime = startTime + positionDiff;
        alignedPeriod = alignment.period;
    }

    return {
        startTime,
        positionTime,
        endTime,
        alignedPeriod,
    };
};

export const findAlignment = (alignments: TimeAlignment[], tagStartTime: number): TimeAlignment | undefined => {
    return alignments.find((alignment) => tagStartTime >= alignment.alignmentTagStartTime);
};

export const getPeriodAlignmentsUsingTag = (
    alignPeriodMap: Map<string, string>,
    periods: DeepActivityPeriod[] | undefined,
    focusFile: FocusFile | undefined,
): TimeAlignment[] =>
    Array.from(alignPeriodMap.entries())
        .map(([periodId, tagId]) => {
            const alignmentPeriod = periods?.find((period) => period.id === periodId);
            const alignmentTag = focusFile?.tags?.find((tag) => tag.tagId === tagId);
            const tagStart = alignmentTag?.tagStart ?? 0;
            const tagOffset = getOffsetInMilliseconds(focusFile, alignmentTag);
            return {
                period: alignmentPeriod,
                alignmentTagStartTime: tagStart + tagOffset,
            };
        })
        .sort((a, b) => a.alignmentTagStartTime - b.alignmentTagStartTime);

export const getPeriodAlignmentsUsingTimingTag = (
    alignPeriodMap: Map<string, string>,
    periods: DeepActivityPeriod[] | undefined,
    focusFile: FocusFile | undefined,
): TimeAlignment[] =>
    Array.from(alignPeriodMap.entries())
        .map(([periodId, timingTagId]) => {
            const alignmentPeriod = periods?.find((period) => period.id === periodId);
            const alignmentTag = focusFile?.timing?.find((timingTag) => timingTag.period_id === timingTagId);

            return {
                period: alignmentPeriod,
                alignmentTagStartTime: alignmentTag?.start_name ?? 0,
            };
        })
        .sort((a, b) => a.alignmentTagStartTime - b.alignmentTagStartTime);

export const getTimeAlignments = (
    selections: ImportSelections,
    activity: DeepActivity | undefined,
    focusFile: FocusFile | undefined,
) => {
    return selections.alignPeriodOption === TIMING_OPTION
        ? getPeriodAlignmentsUsingTimingTag(selections.alignPeriodMap, activity?.periods, focusFile)
        : getPeriodAlignmentsUsingTag(selections.alignPeriodMap, activity?.periods, focusFile);
};
